import React from "react";
import { Link } from "react-router-dom";
import styles from "../components/Dashboard.module.css";
import { agentsApi, type Agent } from "../services/agentsApi";
import { datasetCoverageApi, type AgentDatasetPair } from "../services/datasetCoverageApi";
import { EvalCase, RunPromptItem, RunRow, runsApi } from "../services/runsApi";
import { resolveDefaultDatasetId } from "../services/metricsApi";

type DatasetOption = {
  id: string;
  label: string;
  pairs: AgentDatasetPair[];
};

type SubmitMode = "new_run" | "append";

type CompletedBatch = {
  key: string;
  /** Golden/base dataset used to pick agents in the Evaluate UI. */
  datasetId: string;
  datasetLabel: string;
  /** Langfuse ad-hoc prompt-eval dataset the run actually targeted. */
  adhocDatasetId: string;
  completedAt: string;
  runIds: string[];
  agentIds: string[];
  totalPassed: number;
  totalItems: number;
  itemsPerAgent: number;
};

const newPromptId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

function groupPairsByDataset(pairs: AgentDatasetPair[]): DatasetOption[] {
  const byDataset = new Map<string, DatasetOption>();
  for (const pair of pairs) {
    const existing = byDataset.get(pair.dataset_id);
    if (existing) {
      existing.pairs.push(pair);
      continue;
    }
    byDataset.set(pair.dataset_id, {
      id: pair.dataset_id,
      label: pair.dataset_name?.trim() || pair.dataset_id,
      pairs: [pair],
    });
  }
  return Array.from(byDataset.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function resolveActiveAgentsForDataset(
  dataset: DatasetOption | null,
  agents: Agent[],
): Agent[] {
  if (!dataset) return [];
  const enabled = new Set(agents.filter((agent) => agent.enabled).map((agent) => agent.id));
  const agentIds = Array.from(new Set(dataset.pairs.map((pair) => pair.agent_id))).filter((id) =>
    enabled.has(id),
  );
  return agentIds
    .map((id) => agents.find((agent) => agent.id === id))
    .filter((agent): agent is Agent => agent !== undefined);
}

function formatBatchWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function groupCompletedBatches(runs: RunRow[]): CompletedBatch[] {
  const batches = new Map<string, Map<string, RunRow>>();

  for (const run of runs) {
    if (run.triggerSource === "scheduler") continue;

    const completedAt = run.finishedAt || run.startedAt;
    const minuteKey = completedAt.slice(0, 16);
    const key = run.comparisonId?.trim()
      ? `cmp:${run.comparisonId}`
      : `ds:${run.datasetId}::${minuteKey}`;

    const byAgent = batches.get(key) ?? new Map<string, RunRow>();
    const previous = byAgent.get(run.agentId);
    const previousAt = previous ? previous.finishedAt || previous.startedAt : "";
    if (!previous || completedAt > previousAt) {
      byAgent.set(run.agentId, run);
    }
    batches.set(key, byAgent);
  }

  const grouped: CompletedBatch[] = [];
  Array.from(batches.entries()).forEach(([key, byAgent]) => {
    const agentRuns = Array.from(byAgent.values());
    if (agentRuns.length === 0) return;

    const itemCounts = new Set(agentRuns.map((run) => run.totalItems));
    if (itemCounts.size !== 1) return;

    const itemsPerAgent = agentRuns[0]!.totalItems;
    const completedAt = agentRuns.reduce(
      (latest, run) => {
        const at = run.finishedAt || run.startedAt;
        return at > latest ? at : latest;
      },
      "",
    );

    grouped.push({
      key,
      datasetId: agentRuns[0]!.baseDatasetId || agentRuns[0]!.datasetId,
      datasetLabel:
        agentRuns[0]!.baseDatasetLabel ||
        agentRuns[0]!.datasetLabel ||
        agentRuns[0]!.datasetId,
      adhocDatasetId: agentRuns[0]!.datasetId,
      completedAt,
      runIds: agentRuns.map((run) => run.id),
      agentIds: agentRuns.map((run) => run.agentId),
      totalPassed: agentRuns.reduce((sum, run) => sum + run.passedItems, 0),
      totalItems: itemsPerAgent * agentRuns.length,
      itemsPerAgent,
    });
  });

  return grouped.sort((a, b) => b.completedAt.localeCompare(a.completedAt));
}

function batchLabel(batch: CompletedBatch): string {
  return `${batch.datasetLabel} · ${formatBatchWhen(batch.completedAt)} · ${batch.agentIds.length} agent${batch.agentIds.length === 1 ? "" : "s"} · ${batch.itemsPerAgent} case${batch.itemsPerAgent === 1 ? "" : "s"}/agent · ${batch.totalPassed}/${batch.totalItems} passed`;
}

const normalizePrompt = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, " ");

const uniquePromptTexts = (rows: Array<{ text: string }>): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const row of rows) {
    const text = row.text.trim();
    if (!text) continue;
    const key = normalizePrompt(text);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(text);
  }
  return unique;
};

function promptStatusClass(
  status: RunPromptItem["status"],
  styles: Record<string, string>,
): string {
  if (status === "passed") return styles.evaluateExistingPromptStatus_passed ?? "";
  if (status === "failed") return styles.evaluateExistingPromptStatus_failed ?? "";
  return styles.evaluateExistingPromptStatus_running ?? "";
}

const EvaluateCasesPage: React.FC = () => {
  const [mode, setMode] = React.useState<SubmitMode>("new_run");
  const [pairs, setPairs] = React.useState<AgentDatasetPair[]>([]);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [datasets, setDatasets] = React.useState<DatasetOption[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = React.useState("");
  const [promptRows, setPromptRows] = React.useState<Array<{ id: string; text: string }>>([
    { id: newPromptId(), text: "" },
  ]);
  const [parsedCases, setParsedCases] = React.useState<EvalCase[]>([]);
  const [completedBatches, setCompletedBatches] = React.useState<CompletedBatch[]>([]);
  const [selectedBatchKey, setSelectedBatchKey] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [existingPrompts, setExistingPrompts] = React.useState<RunPromptItem[]>([]);
  const [existingPromptsLoading, setExistingPromptsLoading] = React.useState(false);
  const [existingPromptsSource, setExistingPromptsSource] = React.useState<string | null>(null);

  const selectedDataset = React.useMemo(
    () => datasets.find((dataset) => dataset.id === selectedDatasetId) || null,
    [datasets, selectedDatasetId],
  );

  const activeAgents = React.useMemo(
    () => resolveActiveAgentsForDataset(selectedDataset, agents),
    [selectedDataset, agents],
  );

  const prompts = React.useMemo(() => uniquePromptTexts(promptRows), [promptRows]);

  const selectedBatch = React.useMemo(
    () => completedBatches.find((batch) => batch.key === selectedBatchKey) || null,
    [completedBatches, selectedBatchKey],
  );

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [pairsRes, agentsRes] = await Promise.all([
          datasetCoverageApi.listPairs(),
          agentsApi.listAgents(),
        ]);
        if (!mounted) return;
        const grouped = groupPairsByDataset(pairsRes.data);
        setPairs(pairsRes.data);
        setAgents(agentsRes);
        setDatasets(grouped);
        setSelectedDatasetId(resolveDefaultDatasetId(grouped));
      } catch (e: unknown) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load evaluate options.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (mode !== "append") {
      setCompletedBatches([]);
      setSelectedBatchKey("");
      return;
    }
    let mounted = true;
    const loadBatches = async () => {
      try {
        const res = await runsApi.listRuns({
          status: "completed",
          source: "manual",
          prompt_eval_only: true,
          limit: 100,
        });
        if (!mounted) return;
        const batches = groupCompletedBatches(res.runs);
        setCompletedBatches(batches);
        setSelectedBatchKey(batches[0]?.key || "");
      } catch {
        if (!mounted) return;
        setCompletedBatches([]);
        setSelectedBatchKey("");
      }
    };
    void loadBatches();
    return () => {
      mounted = false;
    };
  }, [mode]);

  React.useEffect(() => {
    if (mode === "append" && selectedBatch) {
      setSelectedDatasetId(selectedBatch.datasetId);
    }
  }, [mode, selectedBatch]);

  React.useEffect(() => {
    if (mode !== "append") {
      setExistingPrompts([]);
      setExistingPromptsSource(null);
      setExistingPromptsLoading(false);
      return;
    }
    if (!selectedBatch?.runIds[0]) {
      setExistingPrompts([]);
      setExistingPromptsSource(null);
      setExistingPromptsLoading(false);
      return;
    }

    let mounted = true;
    const loadExistingPrompts = async () => {
      setExistingPrompts([]);
      setExistingPromptsSource(null);
      setExistingPromptsLoading(true);
      try {
        const runId = selectedBatch.runIds[0]!;
        const prompts = await runsApi.getRunPrompts(runId);
        if (!mounted) return;
        setExistingPrompts(prompts);
        setExistingPromptsSource(batchLabel(selectedBatch));
      } catch {
        if (!mounted) return;
        setExistingPrompts([]);
        setExistingPromptsSource(null);
      } finally {
        if (mounted) setExistingPromptsLoading(false);
      }
    };
    void loadExistingPrompts();
    return () => {
      mounted = false;
    };
  }, [mode, selectedBatch]);

  const updatePrompt = (id: string, text: string) => {
    setPromptRows((rows) => rows.map((row) => (row.id === id ? { ...row, text } : row)));
    setParsedCases([]);
  };

  const addPromptRow = () => {
    setPromptRows((rows) => [...rows, { id: newPromptId(), text: "" }]);
    setParsedCases([]);
  };

  const removePromptRow = (id: string) => {
    setPromptRows((rows) => {
      const next = rows.filter((row) => row.id !== id);
      return next.length > 0 ? next : [{ id: newPromptId(), text: "" }];
    });
    setParsedCases([]);
  };

  const previewCases = async () => {
    if (prompts.length === 0) {
      setError("Add at least one prompt before previewing.");
      return;
    }
    if (mode === "new_run" && !selectedDataset) {
      setError("Choose a dataset first so matching prompts can reuse verified gold.");
      return;
    }
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const datasetName =
        mode === "append"
          ? selectedBatch?.datasetLabel || selectedDataset?.label
          : selectedDataset?.label;
      const datasetId =
        mode === "append"
          ? selectedBatch?.datasetId || selectedDataset?.id
          : selectedDataset?.id;
      const result = await runsApi.parsePrompts(prompts, {
        datasetName: datasetName || undefined,
        datasetId: datasetId || undefined,
      });
      setParsedCases(result.cases);
      const verified = result.cases.filter((item) => item.evaluation_basis !== "judge_only").length;
      const rateLimited = result.cases.filter(
        (item) => item.reference_source === "parser_rate_limit_fallback",
      ).length;
      setFeedback(
        `Parsed ${result.cases.length} case(s)` +
          (verified > 0 ? ` · ${verified} matched verified dataset gold` : "") +
          (rateLimited > 0
            ? ` · ${rateLimited} used a local fallback because OpenAI was rate-limited`
            : "") +
          `. Review below before submitting.`,
      );
    } catch (e: unknown) {
      setParsedCases([]);
      setError(e instanceof Error ? e.message : "Failed to parse prompts.");
    } finally {
      setBusy(false);
    }
  };

  const submitEvaluation = async () => {
    if (prompts.length === 0 && parsedCases.length === 0) {
      setError("Add at least one prompt or preview parsed cases first.");
      return;
    }

    setBusy(true);
    setError(null);
    setFeedback(null);

    try {
      let cases = parsedCases;
      if (cases.length === 0) {
        const datasetName =
          mode === "append"
            ? selectedBatch?.datasetLabel || selectedDataset?.label
            : selectedDataset?.label;
        const datasetId =
          mode === "append"
            ? selectedBatch?.datasetId || selectedDataset?.id
            : selectedDataset?.id;
        const parsed = await runsApi.parsePrompts(prompts, {
          datasetName: datasetName || undefined,
          datasetId: datasetId || undefined,
        });
        cases = parsed.cases;
        setParsedCases(cases);
      }
      const payload = { cases };
      const caseCount = cases.length;

      if (mode === "new_run") {
        if (!selectedDataset) {
          setError("Choose a dataset first.");
          return;
        }
        if (activeAgents.length === 0) {
          setError("No enabled agents are connected to this dataset.");
          return;
        }

        const comparisonId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}`;

        const results = await Promise.allSettled(
          activeAgents.map((agent) =>
            runsApi.launchRun({
              agent_id: agent.id,
              dataset_id: selectedDataset.id,
              dataset_name: selectedDataset.label,
              comparison_id: comparisonId,
              source: "manual",
              ...payload,
            }),
          ),
        );

        const succeeded = results.filter((result) => result.status === "fulfilled");
        const failed = results.length - succeeded.length;
        if (succeeded.length === 0) {
          const firstError = results.find((result) => result.status === "rejected") as
            | PromiseRejectedResult
            | undefined;
          throw firstError?.reason instanceof Error
            ? firstError.reason
            : new Error("Failed to queue runs for all agents.");
        }

        setFeedback(
          `Queued ${succeeded.length} prompt-eval run(s) for agents on ${selectedDataset.label} (${caseCount} case(s) each)${failed > 0 ? `; ${failed} agent(s) failed.` : "."}`,
        );
      } else {
        if (!selectedBatch || selectedBatch.runIds.length === 0) {
          setError("Select a completed evaluation batch to append to.");
          return;
        }

        const comparisonId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}`;

        const results = await Promise.allSettled(
          selectedBatch.runIds.map((runId) =>
            runsApi.appendCases(runId, { ...payload, comparison_id: comparisonId }),
          ),
        );
        const succeeded = results.filter((result) => result.status === "fulfilled");
        const failed = results.length - succeeded.length;
        if (succeeded.length === 0) {
          const firstError = results.find((result) => result.status === "rejected") as
            | PromiseRejectedResult
            | undefined;
          throw firstError?.reason instanceof Error
            ? firstError.reason
            : new Error("Failed to append cases to the selected batch.");
        }

        setFeedback(
          `Appended ${caseCount} case(s) to ${selectedBatch.datasetLabel} — queued ${succeeded.length} new forked run(s)${failed > 0 ? `; ${failed} failed.` : "."}`,
        );
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to submit evaluation.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <section className={styles.dashboardLoading} aria-live="polite" aria-busy="true">
          <div className={styles.dashboardLoadingSpinner} />
          <p className={styles.dashboardLoadingText}>Loading evaluate options...</p>
        </section>
      </div>
    );
  }

  const appendAgentsForBatch = selectedBatch
    ? Array.from(new Set(selectedBatch.agentIds))
    : activeAgents.map((agent) => agent.id);

  return (
    <div className={styles.evaluatePage}>
      <header className={styles.evaluateHero}>
        <h1 className={styles.evaluateHeroTitle}>Evaluate Cases</h1>
        <p className={styles.evaluateHeroSubtitle}>
          Add prompts as individual cases, then evaluate them across every enabled agent connected
          to the selected dataset. Cases are stored in a separate prompt-eval Langfuse dataset — the
          golden/ready dataset is never modified. Append mode adds new cases to an existing
          completed prompt-eval batch without re-running older items.
        </p>
      </header>

      {pairs.length === 0 ? (
        <section className={styles.evaluateSection}>
          <div className={styles.metricsErrorBox}>
            <p>No connected pairs found. Connect datasets to agents in the Agents tab first.</p>
          </div>
        </section>
      ) : (
        <>
          <section className={styles.evaluateSection}>
            <div className={styles.evaluateSectionHeader}>
              <h2 className={styles.evaluateSectionTitle}>Prompts</h2>
            </div>
            <p className={styles.evaluateSectionHint}>
              Each prompt becomes one evaluation case. Use Add prompt to build a batch before
              submitting.
            </p>
            <div className={styles.evaluatePromptList}>
              {promptRows.map((row, index) => (
                <div key={row.id} className={styles.evaluatePromptRow}>
                  <span className={styles.evaluatePromptIndex} aria-hidden="true">
                    {index + 1}
                  </span>
                  <textarea
                    className={styles.evaluatePromptInput}
                    rows={2}
                    placeholder="Describe a test case in plain language…"
                    value={row.text}
                    onChange={(e) => updatePrompt(row.id, e.target.value)}
                    disabled={busy}
                    aria-label={`Prompt ${index + 1}`}
                  />
                  <button
                    type="button"
                    className={styles.evaluatePromptRemove}
                    onClick={() => removePromptRow(row.id)}
                    disabled={busy || promptRows.length === 1}
                    aria-label={`Remove prompt ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={styles.evaluateAddPromptBtn}
                onClick={addPromptRow}
                disabled={busy}
              >
                + Add prompt
              </button>
            </div>
          </section>

          <section className={styles.evaluateSection}>
            <div className={styles.evaluateSectionHeader}>
              <h2 className={styles.evaluateSectionTitle}>Run options</h2>
              <div className={styles.evaluateModeTrack} role="tablist" aria-label="Evaluation mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "new_run"}
                  className={`${styles.evaluateModeBtn} ${mode === "new_run" ? styles.evaluateModeBtnActive : ""}`.trim()}
                  onClick={() => setMode("new_run")}
                >
                  New run
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "append"}
                  className={`${styles.evaluateModeBtn} ${mode === "append" ? styles.evaluateModeBtnActive : ""}`.trim()}
                  onClick={() => setMode("append")}
                >
                  Append to run
                </button>
              </div>
            </div>

            {mode === "new_run" ? (
              <>
                <label className={styles.agentsField}>
                  <span>Dataset</span>
                  <select
                    className={styles.metricsInput}
                    value={selectedDatasetId}
                    onChange={(e) => setSelectedDatasetId(e.target.value)}
                    disabled={busy}
                  >
                    {datasets.map((dataset) => (
                      <option key={dataset.id} value={dataset.id}>
                        {dataset.label}
                      </option>
                    ))}
                  </select>
                </label>
                <p className={styles.evaluateSectionHint}>
                  Agents come from connections to this golden dataset. Manual cases are written to a
                  separate prompt-eval dataset (golden stays untouched).
                </p>
                {activeAgents.length > 0 ? (
                  <div className={styles.evaluateAgentsChipRow}>
                    {activeAgents.map((agent) => (
                      <span key={agent.id} className={styles.evaluateAgentChip}>
                        {agent.name || agent.id}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className={styles.metricsErrorBox}>
                    <p>No enabled agents are connected to this dataset.</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <label className={styles.agentsField}>
                  <span>Completed evaluation</span>
                  <select
                    className={styles.metricsInput}
                    value={selectedBatchKey}
                    onChange={(e) => setSelectedBatchKey(e.target.value)}
                    disabled={busy || completedBatches.length === 0}
                  >
                    {completedBatches.length === 0 ? (
                      <option value="">No completed evaluations yet</option>
                    ) : (
                      completedBatches.map((batch) => (
                        <option key={batch.key} value={batch.key}>
                          {batchLabel(batch)}
                        </option>
                      ))
                    )}
                  </select>
                </label>
                {selectedBatch ? (
                  <>
                    <p className={styles.evaluateSectionHint}>
                      Appends only to this batch on {selectedBatch.agentIds.length} agent
                      {selectedBatch.agentIds.length === 1 ? "" : "s"} under{" "}
                      <strong>{selectedBatch.datasetLabel}</strong>.
                    </p>
                    <div className={styles.evaluateAgentsChipRow}>
                      {appendAgentsForBatch.map((agentId) => (
                        <span key={agentId} className={styles.evaluateAgentChip}>
                          {agents.find((agent) => agent.id === agentId)?.name || agentId}
                        </span>
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            )}
          </section>

          {mode === "append" && (existingPromptsLoading || existingPrompts.length > 0) ? (
            <section className={styles.evaluateSection}>
              <div className={styles.evaluateSectionHeader}>
                <h2 className={styles.evaluateSectionTitle}>Prompts in this evaluation</h2>
              </div>
              {existingPromptsSource ? (
                <p className={styles.evaluateSectionHint}>
                  Cases already evaluated in the selected batch. New prompts above are appended on
                  top of these.{" "}
                  <span className={styles.evaluateExistingSource}>{existingPromptsSource}</span>
                </p>
              ) : null}
              {existingPromptsLoading ? (
                <p className={styles.evaluateSectionHint}>Loading existing prompts...</p>
              ) : (
                <ol className={styles.evaluateExistingPromptList}>
                  {existingPrompts.map((item) => (
                    <li key={`${item.index}-${item.question.slice(0, 32)}`} className={styles.evaluateExistingPromptItem}>
                      <span className={styles.evaluateExistingPromptText}>{item.question}</span>
                      <span
                        className={`${styles.evaluateExistingPromptStatus} ${promptStatusClass(item.status, styles)}`.trim()}
                      >
                        {item.status}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ) : null}

          {parsedCases.length > 0 ? (
            <section className={styles.evaluateSection}>
              <h2 className={styles.evaluateSectionTitle}>Parsed preview</h2>
              {parsedCases.some((item) => item.evaluation_basis === "judge_only") ? (
                <p className={styles.evaluateSectionHint}>
                  Some prompts have no high-confidence verified reference. They will receive a
                  separately labeled judge-only quality score and will not affect verified accuracy.
                </p>
              ) : null}
              <div className={styles.evaluateParsedGrid}>
                {parsedCases.map((item, index) => (
                  <article
                    key={item.id || `${index}-${item.question.slice(0, 24)}`}
                    className={styles.evaluateParsedCard}
                  >
                    <p className={styles.evaluateParsedQuestion}>{item.question}</p>
                    <p className={styles.evaluateParsedAnswer}>
                      {item.evaluation_basis === "judge_only"
                        || item.reference_source === "parser_rate_limit_fallback"
                        || item.reference_source === "language_mismatch_fallback"
                        ? "No verified reference (judge-only)"
                        : item.desired_answer}
                    </p>
                    <p className={styles.evaluateSectionHint}>
                      {item.reference_source === "parser_rate_limit_fallback"
                        ? "Judge-only · local fallback (OpenAI rate limit)"
                        : item.evaluation_basis === "judge_only"
                        ? "Judge-only · no verified reference"
                        : `Verified reference · ${item.reference_source ?? "provided"}`}
                    </p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className={styles.evaluateSection}>
            <div className={styles.evaluateActions}>
              <button
                type="button"
                className={styles.metricsSecondaryBtn}
                onClick={previewCases}
                disabled={busy || prompts.length === 0}
              >
                {busy ? "Working..." : "Preview parsed cases"}
              </button>
              <button
                type="button"
                className={styles.agentsPrimaryBtn}
                onClick={submitEvaluation}
                disabled={
                  busy ||
                  prompts.length === 0 ||
                  (mode === "new_run" && activeAgents.length === 0) ||
                  (mode === "append" && !selectedBatch)
                }
              >
                {busy
                  ? "Working..."
                  : mode === "new_run"
                    ? `Start run on ${activeAgents.length} agent${activeAgents.length === 1 ? "" : "s"}`
                    : "Append & evaluate new cases"}
              </button>
              <Link
                className={`${styles.metricsSecondaryBtn} ${styles.evaluateDashboardLink}`.trim()}
                to="/dashboard"
              >
                Open dashboard
              </Link>
            </div>
          </section>
        </>
      )}

      {feedback ? <div className={styles.metricsSuccessBox}>{feedback}</div> : null}
      {error ? (
        <div className={styles.metricsErrorBox}>
          <p>{error}</p>
        </div>
      ) : null}
    </div>
  );
};

export default EvaluateCasesPage;
